/**
 * Document 2 · Phase 10.2 — Admin Pricing Panel.
 *
 * Mounted inside WorkspaceSettings → Billing tab (replaces placeholder for admins).
 *
 * Lets an admin:
 *   - View current prices, current Stripe price IDs, last update time
 *   - Edit any or all tier × interval amounts
 *   - Set an annual discount % (display-only preview)
 *   - Save → calls PUT /billing/admin/pricing which creates Stripe Prices
 *   - View recent audit log of price changes
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import {
  CreditCard, Save, Loader2, RefreshCw, TrendingUp, History, ExternalLink,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';


const TIERS = [
  { id: 'starter',      label: 'Starter' },
  { id: 'professional', label: 'Professional' },
  { id: 'elite',        label: 'Elite' },
];


function fmt(n) {
  if (n === null || n === undefined) return '—';
  return `$${Number(n).toLocaleString()}`;
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}


export default function BillingAdminPanel() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState({});
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pricing, log] = await Promise.all([
        api.get('/billing/admin/pricing'),
        api.get('/billing/admin/audit-log', { limit: 20 }),
      ]);
      setCurrent(pricing);
      // seed draft with current amounts (NOT with the stored amount-discount pct)
      const d = {};
      TIERS.forEach((t) => {
        d[`${t.id}_monthly`] = pricing[`${t.id}_monthly`];
        d[`${t.id}_yearly`] = pricing[`${t.id}_yearly`];
      });
      d.annual_discount_pct = pricing.annual_discount_pct;
      setDraft(d);
      setAudit(log?.entries || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const changedKeys = () => {
    const out = {};
    if (!current) return out;
    TIERS.forEach((t) => {
      ['monthly', 'yearly'].forEach((iv) => {
        const k = `${t.id}_${iv}`;
        if (Number(draft[k]) !== Number(current[k])) {
          out[k] = Number(draft[k]);
        }
      });
    });
    if (Number(draft.annual_discount_pct) !== Number(current.annual_discount_pct)) {
      out.annual_discount_pct = Number(draft.annual_discount_pct);
    }
    return out;
  };

  const hasChanges = Object.keys(changedKeys()).length > 0;

  const onSave = async () => {
    const body = changedKeys();
    if (Object.keys(body).length === 0) return;
    setSaving(true);
    try {
      const res = await api.put('/billing/admin/pricing', body);
      setCurrent(res);
      toast.success('Prices synced to Stripe');
      // Re-fetch audit log so the new row appears
      const log = await api.get('/billing/admin/audit-log', { limit: 20 });
      setAudit(log?.entries || []);
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setDraftVal = (k, v) => setDraft((p) => ({ ...p, [k]: v === '' ? '' : parseInt(v, 10) || 0 }));

  if (loading) {
    return (
      <Card className="rounded-sm border">
        <CardContent className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const stripeOk = !!current?.stripe_configured;

  return (
    <div className="space-y-5" data-testid="billing-admin-panel">
      <Card className="rounded-sm border">
        <CardContent className="p-5 space-y-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-4 h-4 text-[#B89B5E]" />
                <h3 className="text-sm font-semibold uppercase tracking-widest">Plan Pricing</h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Saving creates new Stripe <code className="font-mono text-[10px]">Price</code> objects and points all new signups at them.
                Existing subscribers keep their current subscription until they switch plans.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {stripeOk ? (
                <Badge variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Stripe connected
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-sm border-red-300 text-red-700 bg-red-50 text-[10px]">
                  <AlertTriangle className="w-3 h-3 mr-1" /> STRIPE_API_KEY missing
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={load} className="rounded-sm" data-testid="billing-admin-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-admin-price-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                  <th className="py-2 pr-3">Tier</th>
                  <th className="py-2 pr-3">Monthly ($)</th>
                  <th className="py-2 pr-3">Yearly ($)</th>
                  <th className="py-2 pr-3">Current Stripe Price ID</th>
                </tr>
              </thead>
              <tbody>
                {TIERS.map((t) => (
                  <tr key={t.id} className="border-b last:border-b-0">
                    <td className="py-2.5 pr-3 font-medium align-top">
                      <div>{t.label}</div>
                      <div className="text-[10px] text-muted-foreground font-mono uppercase">{t.id}</div>
                    </td>
                    <td className="py-2.5 pr-3 align-top">
                      <Input
                        type="number" min={1}
                        value={draft[`${t.id}_monthly`] ?? ''}
                        onChange={(e) => setDraftVal(`${t.id}_monthly`, e.target.value)}
                        className="rounded-sm h-9 w-[110px]"
                        data-testid={`billing-admin-${t.id}-monthly`}
                      />
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Current: {fmt(current[`${t.id}_monthly`])}/mo
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 align-top">
                      <Input
                        type="number" min={1}
                        value={draft[`${t.id}_yearly`] ?? ''}
                        onChange={(e) => setDraftVal(`${t.id}_yearly`, e.target.value)}
                        className="rounded-sm h-9 w-[110px]"
                        data-testid={`billing-admin-${t.id}-yearly`}
                      />
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Current: {fmt(current[`${t.id}_yearly`])}/yr
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 align-top min-w-[230px]" data-testid={`billing-admin-${t.id}-price-ids`}>
                      <div className="flex flex-col gap-0.5 font-mono text-[10px] break-all">
                        <span className="text-muted-foreground">m: {current[`${t.id}_monthly_price_id`] || <em className="text-amber-700">not synced</em>}</span>
                        <span className="text-muted-foreground">y: {current[`${t.id}_yearly_price_id`] || <em className="text-amber-700">not synced</em>}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-end justify-between flex-wrap gap-3 pt-2 border-t">
            <div>
              <Label className="text-xs">Annual discount % (display only)</Label>
              <Input
                type="number" min={0} max={50}
                value={draft.annual_discount_pct ?? ''}
                onChange={(e) => setDraft((p) => ({ ...p, annual_discount_pct: parseInt(e.target.value, 10) || 0 }))}
                className="rounded-sm h-9 w-[110px]"
                data-testid="billing-admin-annual-pct"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Shown to customers as "Save X%". Amounts above are the source of truth.
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-[10px] text-muted-foreground">
                Last change: {fmtDate(current?.updated_at)} {current?.updated_by ? `· by ${current.updated_by}` : ''}
              </div>
              <Button
                size="sm"
                onClick={onSave}
                disabled={!hasChanges || saving || !stripeOk}
                className="rounded-sm"
                data-testid="billing-admin-save-btn"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Syncing with Stripe…</>
                  : <><Save className="w-4 h-4 mr-1" /> Save & sync</>
                }
              </Button>
              {!hasChanges && !saving && (
                <div className="text-[10px] text-muted-foreground">No pending changes</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card className="rounded-sm border">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold uppercase tracking-widest">Price change audit log</h3>
          </div>
          {audit.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No pricing changes logged yet. Save a change above to begin an audit trail.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-admin-audit-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Admin</th>
                    <th className="py-2 pr-3">Tier · Interval</th>
                    <th className="py-2 pr-3">Old → New</th>
                    <th className="py-2 pr-3">Stripe IDs</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((row, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-2.5 pr-3 text-muted-foreground">{fmtDate(row.at)}</td>
                      <td className="py-2.5 pr-3 font-medium">{row.admin_email || row.admin_user_id?.slice(0, 8)}</td>
                      <td className="py-2.5 pr-3">
                        <span className="capitalize">{row.tier}</span>
                        <span className="text-muted-foreground"> · {row.interval}</span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="line-through text-muted-foreground">{fmt(row.old_amount)}</span>
                        <TrendingUp className="w-3 h-3 mx-1.5 inline" />
                        <span className="font-semibold text-foreground">{fmt(row.new_amount)}</span>
                      </td>
                      <td className="py-2.5 pr-3 font-mono text-[10px] text-muted-foreground">
                        <div>old: {row.old_price_id || '—'}</div>
                        <div>new: {row.new_price_id}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <ExternalLink className="w-3 h-3" />
        Stripe dashboard: <a href="https://dashboard.stripe.com/test/prices" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">manage Prices (test mode)</a>
      </div>
    </div>
  );
}
